import { asTask } from '@execonline-inc/error-handling';
import { warn } from '@execonline-inc/logging';
import { when } from '@execonline-inc/maybe-adapter';
import { assertNever, noop } from '@kofno/piper';
import { err } from 'resulty';
import { Task } from 'taskarian';
import { AppyError, callApi } from '../../../../Appy';
import { currentUserStore } from '../../../../CurrentUserStore';
import ErrorActionableReaction, { EAProps, handleError } from '../../../../ErrorActionableReaction';
import { MissingLinkError, findLinkT } from '../../../../LinkyLinky';
import { Link } from '../../../../Resource/Types';
import { csvToJson } from '../../../../Utils/CSV';
import { fileDecoder, importedParticipantsResourceDecoder } from './Decoders';
import {
  DataParseError,
  ImportParticipantsOut,
  ParticipantImportFileData,
  ParticipantImportFileRow,
  State,
} from './Types';
import ImportParticipantsStore from './index';

interface Props extends EAProps<ImportParticipantsStore> {}

type ParticipantsError = AppyError | MissingLinkError;

const handleImportError = (store: ImportParticipantsStore) => (error: ParticipantsError) => {
  switch (error.kind) {
    case 'missing-link-error':
      store.error('Import participants link is unavailable');
      break;
    default:
      handleError(store, error);
  }
};

const rowsLengthCheck = (
  data: ParticipantImportFileData,
): Task<DataParseError, ParticipantImportFileData> =>
  new Task((reject, resolve) => {
    when(data.rows.length > 500, {})
      .map(() => reject({ kind: 'verify', message: 'The list should not exceed 500 rows' }))
      .getOrElse(() => resolve(data));

    return noop;
  });

const runFileDecoder =
  (file: File) =>
  (results: unknown): Task<DataParseError, ParticipantImportFileData> =>
    asTask(
      fileDecoder(file)
        .decodeAny(results)
        .orElse((e) => {
          warn(e);
          return err({
            kind: 'decoder',
            message: 'An error occurred, please refresh the page and try again',
          });
        }),
    );

const parseImport = (file: File): Task<DataParseError, ParticipantImportFileData> =>
  csvToJson(file).andThen(runFileDecoder(file));

const importParticipantsEncoder = (
  participants: ParticipantImportFileRow[],
): ImportParticipantsOut => ({
  import_participant: { participants },
});

class ImportParticipantsReactions extends ErrorActionableReaction<
  ImportParticipantsStore,
  State,
  Props
> {
  tester = () => this.props.store.state;
  effect = (state: State) => {
    const { store } = this.props;

    switch (state.kind) {
      case 'waiting':
      case 'loading':
        store.uploadingReady();
        break;
      case 'file-selected':
        parseImport(state.file)
          .andThen(rowsLengthCheck)
          .fork(
            (error: DataParseError) => {
              store.fileSelectFailed(error.message);
              warn(error);
            },
            (data: ParticipantImportFileData) =>
              store.fileSelectSucceeded(data.rows, data.fileName),
          );
        break;
      case 'file-select-failed':
      case 'file-select-succeeded':
        break;
      case 'submit':
        Task.succeed<ParticipantsError, ReadonlyArray<Link>>(currentUserStore.links)
          .andThen(findLinkT('import-participants'))
          .andThen(
            callApi(importedParticipantsResourceDecoder, importParticipantsEncoder(state.rows)),
          )
          .fork(handleImportError(store), store.submitSucceeded);
        break;
      case 'submit-succeeded':
      case 'uploading-ready':
      case 'view-report':
      case 'error':
        break;
      default:
        assertNever(state);
    }
  };
}

export default ImportParticipantsReactions;
